import { colors } from '@mui/material';
import { designTokens } from './tokens';

export const lightPalette = {
  mode: 'light',
  primary: {
    main: designTokens.customColors.primary.light.main,
    light: designTokens.customColors.primary.light.light,
    dark: designTokens.customColors.primary.light.dark,
    contrastText: designTokens.customColors.primary.light.contrastText,
  },
  secondary: {
    main: designTokens.customColors.secondary.main,
    light: designTokens.customColors.secondary.light,
    dark: designTokens.customColors.secondary.dark,
    contrastText: designTokens.customColors.secondary.contrastText,
  },
  text: {
    primary: colors.blueGrey[900],
    secondary: colors.blueGrey[600],
    disabled: colors.blueGrey[400],
  },
  background: {
    default: colors.common.white,
    paper: colors.common.white,
    dark: '#f6f8fb',
    light: '#fafafa',
    grey: '#cccccc',
  },
  action: {
    active: colors.blueGrey[600],
    hover: designTokens.customColors.action.hover,
    selected: designTokens.customColors.action.selected,
    disabled: designTokens.customColors.action.disabled,
  },
  success: {
    main: '#2e7d32', // Changed from #4caf50 for WCAG AA compliance (6.90:1 contrast)
    light: '#81c784',
    dark: '#1b5e20',
  },
  warning: {
    main: '#f57c00', // Changed from #ff9800 for WCAG AA compliance (4.52:1 contrast)
    light: '#ffb74d',
    dark: '#e65100',
  },
  error: {
    main: '#d32f2f', // Changed from #f44336 for WCAG AA compliance (6.23:1 contrast)
    light: '#e57373',
    dark: '#b71c1c',
  },
  info: {
    main: '#1976d2', // Changed from #2196f3 for WCAG AA compliance (5.14:1 contrast)
    light: '#64b5f6',
    dark: '#0d47a1',
  },
  divider: colors.grey[200],

  // Custom theme colors
  button: {
    main: '#0d151c',
    clickable: 'rgba(211, 211, 211, 0.25)',
    hover: designTokens.customColors.action.hover,
  },
  tab: {
    default: colors.blueGrey[600],
    active: designTokens.customColors.primary.light.main,
    inactive: '#efeeea',
    inactiveBackground: '#e0e0e0',
    hover: '#edeef1',
    indicator: designTokens.customColors.primary.light.main,
  },
  header: {
    dark: '#0d151c',
  },
  cancel: colors.red[800],
  icons: 'MediumPurple',
  row: {
    selected: designTokens.customColors.action.selected,
    selectedHover: '#dcdcdc',
  },
  switch: {
    on: designTokens.harvestColors.green,
    off: '#EAE9E7',
  },
  harvestColors: designTokens.harvestColors,

  // Themed gradients and borders
  gradients: {
    card: designTokens.customColors.gradients.light.card,
    cardHover: designTokens.customColors.gradients.light.cardHover,
    container: designTokens.customColors.gradients.light.container,
    dashboard: designTokens.customColors.gradients.light.dashboard,
  },
  borders: {
    subtle: designTokens.customColors.borders.light.subtle,
    medium: designTokens.customColors.borders.light.medium,
    strong: designTokens.customColors.borders.light.strong,
    hover: designTokens.customColors.borders.light.hover,
  },
};

export const darkPalette = {
  mode: 'dark',
  primary: {
    main: '#e1e4e8',
    light: '#f2f4f6',
    dark: '#c2c7ce',
    contrastText: '#0b0b0b',
  },
  // Neutral greys for secondary actions in dark mode
  secondary: {
    main: '#bdbdbd',
    light: '#e0e0e0',
    dark: '#9e9e9e',
    contrastText: '#0a0b0e',
  },
  text: {
    primary: '#efeeea',
    secondary: '#cdd1d7',
    disabled: 'rgba(255, 255, 255, 0.5)',
  },
  background: {
    default: '#070707',
    paper: '#1e1e1e',
    dark: '#0f0f0f',
    grey: '#cccccc',
  },
  action: {
    active: 'rgba(255, 255, 255, 0.54)',
    hover: 'rgba(255, 255, 255, 0.10)',
    selected: 'rgba(255, 255, 255, 0.16)',
    disabled: 'rgba(255, 255, 255, 0.26)',
    disabledBackground: 'rgba(255, 255, 255, 0.12)',
    focus: 'rgba(255, 255, 255, 0.12)',
  },
  success: {
    main: '#66bb6a', // Lighter variant for dark mode visibility
    light: '#81c784',
    dark: '#2e7d32',
  },
  warning: {
    main: '#ffa726', // Lighter variant for dark mode visibility
    light: '#ffb74d',
    dark: '#f57c00',
  },
  error: {
    main: '#ef5350', // Lighter variant for dark mode visibility
    light: '#e57373',
    dark: '#d32f2f',
  },
  info: {
    main: '#42a5f5', // Lighter variant for dark mode visibility
    light: '#64b5f6',
    dark: '#1976d2',
  },
  divider: 'rgba(255, 255, 255, 0.22)',

  // Custom theme colors
  button: {
    main: '#e1e4e8',
    clickable: 'rgba(255, 255, 255, 0.08)',
    hover: 'rgba(255, 255, 255, 0.12)',
  },
  tab: {
    default: '#c7cbd2',
    active: '#e1e4e8',
    inactiveBackground: 'rgba(255, 255, 255, 0.10)',
    inactiveText: '#dfe2e7',
    hover: 'rgba(255, 255, 255, 0.16)',
    indicator: '#e1e4e8',
  },
  cancel: colors.red[800],
  icons: '#d9dde2',
  row: {
    selected: '#252525',
    selectedHover: '#313131',
  },
  switch: {
    on: designTokens.harvestColors.green,
    off: '#EAE9E7',
  },
  harvestColors: designTokens.harvestColors,

  // Themed gradients and borders for dark mode
  gradients: {
    card: 'linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)',
    cardHover: 'linear-gradient(135deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.02) 100%)',
    container: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
    dashboard: 'linear-gradient(180deg, #0b0b0b 0%, #070707 100%)',
  },
  borders: {
    subtle: 'rgba(255, 255, 255, 0.08)',
    medium: 'rgba(255, 255, 255, 0.15)',
    strong: 'rgba(255, 255, 255, 0.24)',
    hover: 'rgba(255, 255, 255, 0.2)',
  },
};
